import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AssignmentDTOIsCreativeDTO, VisualListResponseIsCreativeResponse } from 'utils/typeguards'
import { FileAPIEntry, FileAction, FileState, useFileAPI } from 'components/common/FileAPI'
import { SignedURLDTO, UrlDTO } from 'models/visuals'
import { UploadType, getSignedURLRetrieverBatch } from 'components/common/FileAPI/fileUtils'
import { getUploadConcurrency } from 'components/common/FileAPI/uploadPool'
import { invalidateAssignmentVisuals, useGetAssignment, useGetAssignmentVisuals } from 'dataQueries'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { isRetryableRequestFailure, retryRequestUntilSuccess, useAPI } from 'utils/API'

import { ProductKind } from 'constants/product'
import { StatusResponse } from 'models/redux'
import axios from 'axios'
import { VisualFileType } from 'constants/visual'
import { VisualUploadEndpoints } from 'constants/API'
import { choosePrimaryProduct } from 'utils/product'
import { isDeliveredVisual, isFailedUpload } from './creativeUploadGating'
import constate from 'constate'
import { useEventTimer } from 'utils/analytics/useTimeEventTracker'
import { logger } from 'utils/logging'
import { useQueryClient } from '@tanstack/react-query'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * The RAW listing reports `10514708-D-backbonephoto-001` where the upload path stores
 * `10514708-D-backbonephoto-001.arw`, so the two only line up once the extension is off. A raw + jpeg
 * pair of the same basename would collapse to one name here, but the listing already cannot tell them
 * apart either — it is the same server-side ambiguity, not one this introduces.
 */
const stripFileExtension = (name: string) => name.replace(/\.[^./]+$/, '')

/** Server names carry a 1-based `backbonephoto-%03d` index that widens past 999 and may trail a `v01`/`_Final` suffix, so it is parsed rather than compared as text */
const serverIndex = (name: string) => {
  const match = stripFileExtension(name).match(/-backbonephoto-(\d+)(?:\D\w*)?$/)
  return match ? Number(match[1]) : undefined
}

// Clears any real index, so a name without one sorts below every indexed visual
const UNINDEXED_ORDER_OFFSET = 1_000_000

export enum CreativeUploadFolder {
  REQUIRED_VISUALS = 'REQUIRED_VISUALS',
  ADDITIONAL_VISUALS = 'ADDITIONAL_VISUALS',
}

interface UploadTimedEvent {
  assignmentId: string
  userEmail: string
  userId: string
  folder: CreativeUploadFolder
  amountOfVisuals: number
  totalSizeB: number
  timeMS: number
  // Both folders share one upload pool, so timeMS includes waiting behind a batch that was already running at drop
  startedDuringOtherUpload: boolean
  uploadConcurrency: number
}

interface HydratedEvent {
  assignmentId: string
  userEmail: string
  userId: string
  listingSize: number
  seededCount: number
  skippedCount: number
  unindexedCount: number
  elapsedMS: number
}

/** One seeding pass, held until its thumbnail loads settle so the event can carry hydration latency */
interface PendingHydration {
  startedAt: number
  listingSize: number
  seededCount: number
  skippedCount: number
  unindexedCount: number
  loadsStarted: boolean
}

/**
 * An upload only reports through a batch-level `onSettled`, so its thumbnail has to be fetched the moment
 * its own bytes land -- waiting for the batch leaves every tile blank until the slowest file in it finishes.
 */
const isLandedUpload = (file: FileAPIEntry) =>
  (file.action === FileAction.UPLOAD || file.action === FileAction.RE_UPLOAD) && file.state === FileState.SUCCESS && !file.displayUrl

export const [CreativeUploadPageContext, useCreativeUploadPage] = constate(({ assignmentId }: { assignmentId: string }) => {

  const { baseUserData } = useUserData()
  const timerCtrl = useEventTimer()
  const queryClient = useQueryClient()

  const api = useAPI<string>()
  const apiRef = useRef(api)
  apiRef.current = api

  const visualsRequirementsQuery = useGetAssignmentVisuals(assignmentId, VisualFileType.RAW)
  const assignmentQuery = useGetAssignment(assignmentId)

  // Keyed by assignment: the route re-renders rather than remounts on an :id change, so a constant scope would show one assignment's visuals in another's grid
  const fileCtrl = useFileAPI(`CT_UPLOAD_PAGE:${assignmentId}`, {
    uploadUrlResolver: getSignedURLRetrieverBatch((entries, api) => api.post<Record<string, SignedURLDTO>>(
      VisualUploadEndpoints.GET_UPLOAD_URL_BATCH,
      { assignmentId },
      entries.map(entry => ({
        filename: entry.fileObject.name,
        contentType: entry.fileObject.type,
        type: VisualFileType.RAW,
      })),
      true,
      {
        params: {
          uploadType: UploadType.RESUMABLE
        }
      }
    )),
    reUploadUrlResolver: (entry, api) => api.put<SignedURLDTO>(
      VisualUploadEndpoints.GET_UPLOAD_URL,
      { assignmentId },
      {
        filename: entry.gcFilename,
        contentType: entry.fileObject.type,
        replacementFilename: entry.fileObject.name,
        type: VisualFileType.RAW,
      },
      true,
      {
        params: {
          uploadType: UploadType.RESUMABLE
        }
      }
    ),
    loadUrlResolver: (entry, api) => api.post<UrlDTO>(
      VisualUploadEndpoints.GET_LOAD_URL,
      {
        assignmentId,
        filename: entry.gcFilename!,
      },
      {},
      true,
      {
        params: {
          type: VisualFileType.RAW_THUMB
        }
      }
    ),
    deleteHandler: async (entry, api) => {
      const response = await api.delete<StatusResponse>(
        VisualUploadEndpoints.DELETE,
        {
          assignmentId,
          filename: entry.gcFilename!,
        },
        true,
        {
          params: {
            type: VisualFileType.RAW
          }
        }
      )

      // Without this the listing stays cached, and the next mount seeds the visual that was just deleted back into the grid
      invalidateAssignmentVisuals(queryClient, assignmentId, VisualFileType.RAW)

      return response
    }
  })

  const [isSubmitOpen, setIsSubmitOpen] = useState(false)

  const namesSeenInListing = useRef(new Set<string>())
  const pendingHydration = useRef<PendingHydration | null>(null)
  const hasReportedHydration = useRef(false)

  /** Closes one seeding pass: without this, every failure mode of the hydration path -- empty listing, all names known, a name filtered as a live upload -- is the same silent no-op */
  const reportHydration = useCallback((pass: PendingHydration) => {
    pendingHydration.current = null
    hasReportedHydration.current = true

    if (!baseUserData?.email || !baseUserData.id) return

    logAnalyticsEvent<HydratedEvent>(
      AnalyticsEvent.CT_UPLOAD_HYDRATED,
      {
        assignmentId,
        listingSize: pass.listingSize,
        seededCount: pass.seededCount,
        skippedCount: pass.skippedCount,
        unindexedCount: pass.unindexedCount,
        elapsedMS: Date.now() - pass.startedAt,
        userEmail: baseUserData.email,
        userId: baseUserData.id,
      }
    )
  }, [assignmentId, baseUserData?.email, baseUserData?.id])

  /*
    An upload's entry is keyed by a temporary id until its mint returns, while the backend reserves the
    blob at mint time -- so between those two points the filename is in the listing but not in the
    inventory, and seeding it creates a duplicate the mint then overwrites, leaving the seeded entry's
    thumbnail load to write SUCCESS/ERROR onto the live upload. Waiting costs a raw published mid-drop
    its tile until the drop finishes; nothing is lost, because a pass that bails records no names.
  */
  const isAnyUploadRunning = useMemo(
    () => fileCtrl.allFilesArray.some((entry) => entry.state === FileState.RUNNING && (entry.action === FileAction.UPLOAD || entry.action === FileAction.RE_UPLOAD)),
    [fileCtrl.allFilesArray]
  )

  // A refetched listing adds raws published meanwhile; names already in the inventory or seen before are skipped so in-session moves survive and in-session deletes never come back
  useEffect(() => {
    const listing = visualsRequirementsQuery.data?.data
    if (!listing || isAnyUploadRunning) return

    // The listing reports names without a file extension while an upload keys its entry by the signed-URL
    // filename, which keeps one — comparing them raw makes every in-session upload look like a new visual
    const knownNames = new Set(fileCtrl.allFilesArray.flatMap((file) => file.gcFilename ? [stripFileExtension(file.gcFilename)] : []))

    const visuals = [...(listing.visuals ?? [])]
    const fresh = visuals.filter((visual) => {
      const name = stripFileExtension(visual.name)
      return !namesSeenInListing.current.has(name) && !knownNames.has(name)
    })
    for (const visual of visuals) namesSeenInListing.current.add(stripFileExtension(visual.name))
    const unindexedCount = visuals.filter((visual) => serverIndex(visual.name) === undefined).length

    if (!fresh.length) {
      // A pass that seeds nothing has no loads to wait for; reported once per mount so an empty listing is
      // distinguishable from an already-hydrated one, without a focus refetch emitting on every tick
      if (!hasReportedHydration.current) reportHydration({ startedAt: Date.now(), listingSize: visuals.length, seededCount: 0, skippedCount: visuals.length, unindexedCount, loadsStarted: false })
      return
    }

    // Keyed on the index in the server filename, not the listing position: positions shift whenever a name
    // drops out between passes. Negative keeps every seeded visual below the in-session uploads, which keep
    // the FileAPI generator's positive values.
    const orderByName = new Map(visuals.map((visual, position) => [visual.name, -(serverIndex(visual.name) ?? UNINDEXED_ORDER_OFFSET + position)]))

    pendingHydration.current = {
      startedAt: pendingHydration.current?.startedAt ?? Date.now(),
      listingSize: visuals.length,
      seededCount: (pendingHydration.current?.seededCount ?? 0) + fresh.length,
      skippedCount: visuals.length - fresh.length,
      unindexedCount,
      loadsStarted: false,
    }

    fileCtrl.mergeAddFiles(fresh.map((visual) => ({
      id: visual.name,
      gcFilename: visual.name,
      originalFilename: visual.originalName,
      tag: CreativeUploadFolder.REQUIRED_VISUALS,
      order: orderByName.get(visual.name),
    })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualsRequirementsQuery.data?.data, isAnyUploadRunning])

  // Counted rather than filtered in the effect, so a progress tick (which rebuilds allFilesArray ~30x/s per running file) cannot re-run either effect below
  const unloadedCount = useMemo(() => fileCtrl.allFilesArray.filter((file) => file.action === FileAction.INIT).length, [fileCtrl.allFilesArray])
  const loadsInFlightCount = useMemo(() => fileCtrl.allFilesArray.filter((file) => file.action === FileAction.LOAD && file.state === FileState.RUNNING).length, [fileCtrl.allFilesArray])
  const landedUploadCount = useMemo(() => fileCtrl.allFilesArray.filter(isLandedUpload).length, [fileCtrl.allFilesArray])

  // loadFiles reads the inventory ref, which only sees entries added by mergeAddFiles on the next render.
  // Covers both entries seeded from the listing (INIT) and uploads as each one lands: uploadFiles only reports
  // through a batch-level onSettled, so waiting for it leaves every tile blank until the slowest file finishes
  useEffect(() => {
    const unloaded = fileCtrl.allFilesArray.filter((file) => file.action === FileAction.INIT || isLandedUpload(file)).map((file) => file.id)
    if (!unloaded.length) return

    // Marked here rather than by observing a RUNNING render: loads that resolve inside the same React batch never produce one
    if (pendingHydration.current) pendingHydration.current.loadsStarted = true

    fileCtrl.loadFiles(unloaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unloadedCount, landedUploadCount])

  // Hydration is over once nothing is waiting to load and nothing is loading -- every seeded entry has a thumbnail, an error tile, or has been deleted
  useEffect(() => {
    const pass = pendingHydration.current
    if (!pass?.loadsStarted || unloadedCount > 0 || loadsInFlightCount > 0) return

    reportHydration(pass)
  }, [unloadedCount, loadsInFlightCount, reportHydration])

  const assignmentData = useMemo(() => {
    if (!assignmentQuery.data?.data || !AssignmentDTOIsCreativeDTO(assignmentQuery.data.data)) return

    return assignmentQuery.data.data
  }, [assignmentQuery?.data?.data])

  const assignmentProduct = useMemo(() => {
    if (!assignmentData?.products) return

    return choosePrimaryProduct(assignmentData.products)
  }, [assignmentData?.products])

  const gridItemSize = useMemo(() => {
    if (assignmentProduct?.kind === ProductKind.GROUND_PHOTO_PRESTIGE) return 12 / 5
    else return 4
  }, [assignmentProduct?.kind])

  const requiredVisualAmount = useMemo(() => {
    const visualData = visualsRequirementsQuery.data?.data
    if (!visualData || !VisualListResponseIsCreativeResponse(visualData)) return 0

    let baseRequiredAmount = 0

    // Calculate the amount of visuals for the product
    if (assignmentProduct?.kind === ProductKind.GROUND_PHOTO_PRESTIGE) baseRequiredAmount = (assignmentData?.creativeInstructionAttributeX ?? 0) * 5
    else baseRequiredAmount = (assignmentData?.creativeInstructionAttributeX ?? 0) * 3

    // Fall back to the server count the gallery used: an attributeX of 0 makes this 0, which disables Submit in both
    // directions (no files blocks on !uploadedRequiredVisualsAmount, any file blocks on > requiredVisualAmount) and the
    // gallery is no longer there as an escape hatch
    return baseRequiredAmount * (assignmentProduct?.quantity ?? 1) || visualData.minUploadCount
  }, [assignmentData?.creativeInstructionAttributeX, assignmentProduct, visualsRequirementsQuery.data?.data])

  // Counted the same way the Submit gate counts, so the number the creative reads never claims more
  // than what reached GCS — a failed or cancelled tile stays on screen but does not fill the quota
  const uploadedRequiredVisualsAmount = useMemo(() => (fileCtrl.filesByTag[CreativeUploadFolder.REQUIRED_VISUALS] ?? []).filter(isDeliveredVisual).length, [fileCtrl.filesByTag])
  const uploadedAdditionalVisualsAmount = useMemo(() => (fileCtrl.filesByTag[CreativeUploadFolder.ADDITIONAL_VISUALS] ?? []).filter(isDeliveredVisual).length, [fileCtrl.filesByTag])

  const areVisualsMissing = useMemo(() => requiredVisualAmount > uploadedRequiredVisualsAmount, [requiredVisualAmount, uploadedRequiredVisualsAmount])
  const isOverRequiredLimit = useMemo(() => requiredVisualAmount > 0 && uploadedRequiredVisualsAmount > requiredVisualAmount, [requiredVisualAmount, uploadedRequiredVisualsAmount])
  const isAnyFileErroneous = useMemo(() => fileCtrl.allFilesArray.some((entry) => !isDeliveredVisual(entry)), [fileCtrl.allFilesArray])

  // Display file prep
  const requiredVisualsForDisplay = useMemo(() => (fileCtrl.filesByTag[CreativeUploadFolder.REQUIRED_VISUALS] ?? []).sort((fileA, fileB) => fileB.order - fileA.order), [fileCtrl.filesByTag])
  const additionalVisualsForDisplay = useMemo(() => (fileCtrl.filesByTag[CreativeUploadFolder.ADDITIONAL_VISUALS] ?? []).sort((fileA, fileB) => fileB.order - fileA.order), [fileCtrl.filesByTag])

  // Counts files still uploading, not only delivered ones, so a creative over-delivering can drop the extras while the required batch is in flight
  const droppedRequiredVisualsAmount = useMemo(() => requiredVisualsForDisplay.filter((entry) => !isFailedUpload(entry)).length, [requiredVisualsForDisplay])
  const isAdditionalVisualsSectionOpen = useMemo(() => droppedRequiredVisualsAmount >= requiredVisualAmount || additionalVisualsForDisplay.length > 0, [additionalVisualsForDisplay, droppedRequiredVisualsAmount, requiredVisualAmount])

  // Popup preview data
  const imagesForPopupPreview = useMemo(() => [...requiredVisualsForDisplay, ...additionalVisualsForDisplay], [additionalVisualsForDisplay, requiredVisualsForDisplay])

  const [expandedImageIndex, setExpandedImageIndex] = useState<number | undefined>(undefined)

  const expandedImageFileEntry = useMemo(() => {
    if (expandedImageIndex === undefined) return null
    return imagesForPopupPreview[expandedImageIndex]
  }, [expandedImageIndex, imagesForPopupPreview])

  const [webUrlsByFilename, setWebUrlsByFilename] = useState<Record<string, string>>({})
  const expandedImageGCFilename = expandedImageFileEntry?.gcFilename

  // The grid only loads thumbnails, so the full-screen preview fetches the web-size variant of the one visual it opens
  useEffect(() => {
    if (!expandedImageGCFilename || webUrlsByFilename[expandedImageGCFilename]) return

    // Browsing a large grid opens one preview per keypress; without this the abandoned polls keep
    // running for their full budget in the background
    const abortController = new AbortController()

    retryRequestUntilSuccess<UrlDTO>(
      () => apiRef.current.post<UrlDTO>(
        VisualUploadEndpoints.GET_LOAD_URL,
        {
          assignmentId,
          filename: expandedImageGCFilename,
        },
        {},
        true,
        {
          signal: abortController.signal,
          params: {
            type: VisualFileType.RAW_WEB
          }
        }
      ),
      2000,
      30,
      isRetryableRequestFailure
    ).then((response) => {
      if (abortController.signal.aborted) return

      // retryRequestUntilSuccess hands back whatever was thrown, and `in` throws on a primitive
      if (!response || typeof response !== 'object' || axios.isAxiosError(response) || !('data' in response)) {
        logger.warn('Web-size preview URL could not be retrieved, keeping the thumbnail', null, {
          context: {
            component: 'CreativeUpload',
            action: 'loadWebPreview',
            metadata: { assignmentId, filename: expandedImageGCFilename },
          },
        })
        return
      }

      setWebUrlsByFilename((current) => ({ ...current, [expandedImageGCFilename]: response.data.url }))
    })

    return () => abortController.abort()
  }, [assignmentId, expandedImageGCFilename, webUrlsByFilename])

  const expandedImageWebUrl = expandedImageGCFilename ? webUrlsByFilename[expandedImageGCFilename] : undefined

  // Analytics
  const logTimedUpload = useCallback((files: File[], folder: CreativeUploadFolder, time: number, startedDuringOtherUpload: boolean) => {

    if (!baseUserData?.email || !baseUserData.id) return

    const totalSize = files.reduce((sum, file) => sum + (file.size ?? 0), 0)

    logAnalyticsEvent<UploadTimedEvent>(
      AnalyticsEvent.CT_UPLOAD_V2_TIMED,
      {
        folder,
        amountOfVisuals: files.length,
        totalSizeB: totalSize,
        assignmentId,
        timeMS: time,
        userEmail: baseUserData?.email,
        userId: baseUserData?.id,
        startedDuringOtherUpload,
        uploadConcurrency: getUploadConcurrency(),
      }
    )

  }, [assignmentId, baseUserData?.email, baseUserData?.id])

  // Handle index overflow -- layoutEffect used to fix indexing before render phase to avoid flickering
  useLayoutEffect(() => {
    if (expandedImageIndex === undefined) return

    if (expandedImageIndex < 0) {
      setExpandedImageIndex(0)
      return
    }

    if (expandedImageIndex > imagesForPopupPreview.length - 1) {
      setExpandedImageIndex(imagesForPopupPreview.length - 1)
      return
    }
  }, [expandedImageIndex, imagesForPopupPreview.length])

  return {
    imagesForPopupPreview,
    setExpandedImageIndex,
    expandedImageIndex,
    expandedImageFileEntry,
    expandedImageWebUrl,
    fileCtrl,
    isAnyFileErroneous,
    assignmentId,
    isSubmitOpen,
    requiredVisualAmount,
    areVisualsMissing,
    assignmentData,
    assignmentProduct,
    isOverRequiredLimit,
    uploadedRequiredVisualsAmount,
    uploadedAdditionalVisualsAmount,
    visualsRequirementsQuery,
    requiredVisualsForDisplay,
    additionalVisualsForDisplay,
    droppedRequiredVisualsAmount,
    isAdditionalVisualsSectionOpen,
    assignmentQuery,
    gridItemSize,
    timerCtrl,
    logTimedUpload,
    setIsSubmitOpen,
  }
})
